'use client';

import {
  createContext,
  useEffect,
  useCallback,
  useContext,
  useState,
  useMemo,
  ReactNode,
} from 'react';
import { UserContext } from '@/_contexts/UserProvider';
import type { Notification } from '@/_types/models/notifications';
import { database } from '@/_libs/firebase';
import { ref, onValue } from 'firebase/database';
import type { NotificationsProviderProps } from './NotificationsProvider.types';
import {
  getNotifications,
  dismissNotification,
  dismissAllNotifications,
  getPaginatedNotifications,
} from '@/_api/admin-notifications';
import { DropLinksContext } from '@/_contexts/DropLinksProvider';
import { sleep } from '@/_utils/functions';

const NotificationsContext = createContext<NotificationsProviderProps>({
  dismissAll: () => null,
  dismissNotificationItem: () => Promise.resolve(),
  isLoading: true,
  isOpen: false,
  isPaginatedLoading: true,
  isUnreadLoading: true,
  loadPaginatedNotifications: () => null,
  notifications: [],
  paginatedNotifications: [],
  reloadNotifications: () => null,
  setOpen: () => null,
  totalCount: 0,
  unreadCount: 0,
  unreadNotifications: [],
  unreadTotalCount: 0,
});

const NotificationsProvider = ({ children }: { children: ReactNode }) => {
  const { token, user } = useContext(UserContext);
  const { loadDropLinks } = useContext(DropLinksContext);

  const [isLoading, setLoading] = useState<boolean>(false);
  const [isPaginatedLoading, setPaginatedLoading] = useState<boolean>(false);
  const [isUnreadLoading, setUnreadLoading] = useState<boolean>(false);
  const [isOpen, setOpen] = useState<boolean>(false);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [notifications, setNotifications] = useState<Array<Notification>>([]);
  const [paginatedNotifications, setPaginatedNotifications] = useState<
    Array<Notification>
  >([]);
  const [unreadNotifications, setUnreadNotifications] = useState<
    Array<Notification>
  >([]);
  const [unreadTotalCount, setUnreadTotalCount] = useState<number>(0);

  const dismissNotificationItem = async (notificationId: number) => {
    try {
      await dismissNotification({ notificationId, token });
      const newNotifications = [...notifications].map((n) => {
        if (n.id === notificationId) n.dismissed = true;
        return n;
      });

      setNotifications(newNotifications);
      setUnreadNotifications(
        unreadNotifications.filter((n) => n.id !== notificationId),
      );
      setPaginatedNotifications(
        paginatedNotifications.map((n) => {
          if (n.id === notificationId) n.dismissed = true;
          return n;
        }),
      );
    } catch (error) {
      console.error(error);
    }
  };

  const reloadNotifications = useCallback(
    async (isSilent?: boolean, delay?: number) => {
      if (!token) return;
      if (delay) await sleep(delay);

      try {
        if (!isSilent) setLoading(true);

        const notifications = await getNotifications({
          token,
        });

        if (!notifications?.error && Array.isArray(notifications)) {
          const orderedNotifications = notifications.sort(
            (a, b) => Date.parse(b.created_at) - Date.parse(a.created_at),
          );

          setNotifications(orderedNotifications);
          setUnreadNotifications(
            orderedNotifications.filter((n) => !n.dismissed),
          );
        }
        // Also grab drop link uploads
        loadDropLinks();
      } catch (error) {
        console.error(error);
      } finally {
        if (!isSilent) setLoading(false);
      }
    },
    [token, loadDropLinks],
  );

  const loadPaginatedNotifications = useCallback(
    async (
      page: number = 0,
      search: string = '',
      isUnreadOnly: boolean,
      selectedType = '',
    ) => {
      if (!token) return;

      try {
        if (isUnreadOnly) {
          setUnreadLoading(true);
        } else {
          setPaginatedLoading(true);
        }

        const { notifications, error, total } = await getPaginatedNotifications(
          {
            page,
            search,
            isUnreadOnly,
            type: selectedType,
            token,
          },
        );

        if (!error && Array.isArray(notifications)) {
          if (isUnreadOnly) {
            setUnreadTotalCount(total);
            setUnreadNotifications(notifications);
          } else {
            setTotalCount(total);
            setPaginatedNotifications(notifications);
          }
        }
      } catch (error) {
        console.error(error);
      } finally {
        if (isUnreadOnly) {
          setUnreadLoading(false);
        } else {
          setPaginatedLoading(false);
        }
      }
    },
    [token],
  );

  const dismissAll = async () => {
    try {
      await dismissAllNotifications({ token });
    } catch (error) {
      console.error(error);
    } finally {
      loadPaginatedNotifications(0, '', false);
      setUnreadNotifications([]);
      reloadNotifications(true);
    }
  };

  const unreadCount: number = useMemo(
    () => notifications?.filter((n) => !n.dismissed)?.length ?? 0,
    [notifications],
  );

  useEffect(() => {
    token && reloadNotifications();
  }, [token, reloadNotifications]);

  // listen on newNotifications channel
  useEffect(() => {
    const _startListening = () => {
      if (!token || !user?.id) return;

      const realTimeMessages = ref(
        database,
        'users/' + user?.id + '/newNotifications',
      );

      const processUnreadMessages = async (_snapshot: any) => {
        reloadNotifications(true);
      };

      onValue(
        realTimeMessages,
        async (_snapshot: any) => await processUnreadMessages(_snapshot),
      );
    };

    token && _startListening();
  }, [token, user?.id]); // eslint-disable-line

  return (
    <NotificationsContext
      value={{
        dismissAll,
        dismissNotificationItem,
        isLoading,
        isOpen,
        isPaginatedLoading,
        isUnreadLoading,
        loadPaginatedNotifications,
        notifications,
        paginatedNotifications,
        unreadNotifications,
        unreadTotalCount,
        reloadNotifications,
        setOpen,
        totalCount,
        unreadCount,
      }}
    >
      {children}
    </NotificationsContext>
  );
};

export { NotificationsProvider, NotificationsContext };
